// Web implementation using MSAL for Azure AD authentication
import { Logger } from '../utils/logger';
import { AuthConfig, authority } from '../config/authConfig';

// Simple MSAL-like implementation for web
interface StoredSession {
  accessToken: string;
  idToken: string;
  accessTokenExpirationDate: string;
  refreshToken?: string;
}

let session: StoredSession | null = null;
let msalClient: any = null;
let msalReady: Promise<void> | null = null;
let renewing: Promise<string | null> | null = null;
/** What Microsoft's full-page sign-in returned on this page load, until AuthContext takes it. */
let redirectSignIn: any = null;

/** A token this close to its expiry is renewed now rather than sent and refused. */
const EXPIRY_MARGIN_MS = 2 * 60 * 1000;

/**
 * MSAL's answers when the browser refuses to open the sign-in popup (strict popup blocking,
 * in-app browsers). It clears its own in-progress state first, so a full-page sign-in can
 * start straight away.
 */
const POPUP_BLOCKED = ['popup_window_error', 'empty_window_error'];

// Initialize MSAL client - once, and on page load too: a browser refresh must be able to renew
// the token silently from MSAL's own cache (bug, 27 Oct 2026 - it was only started by Sign In,
// so after a refresh nothing could renew and every session ended after an hour).
const initMsal = (): Promise<void> => {
  if (!msalReady) {
    msalReady = (async () => {
      try {
        // Require @azure/msal-browser instead of dynamic import
        const { PublicClientApplication } = require('@azure/msal-browser');

        const client = new PublicClientApplication({
          auth: {
            clientId: AuthConfig.clientId,
            authority: authority,
            redirectUri: AuthConfig.redirectUri,
          },
          cache: {
            cacheLocation: 'localStorage',
          },
        });

        await client.initialize();
        // Coming back from the full-page sign-in (signIn's fallback when the popup is
        // blocked): keep the session the popup would have, for AuthContext to finish with.
        let returned: any = null;
        try {
          returned = await client.handleRedirectPromise();
        } catch (redirectErr) {
          // Sent back without a sign-in (cancelled or refused at Microsoft): MSAL itself is
          // fine, and the login screen shows as on any other load.
          Logger.warn('AuthService', 'Full-page sign-in did not complete', redirectErr);
        }
        if (returned) {
          if (returned.account) client.setActiveAccount(returned.account);
          saveSession(returned);
          redirectSignIn = returned;
          Logger.info('AuthService', '✓ Back from Microsoft sign-in');
        }
        msalClient = client;
        Logger.info('AuthService', '✓ MSAL initialized');
      } catch (err) {
        Logger.warn('AuthService', 'MSAL not available, using fallback auth', err);
      }
    })();
  }
  return msalReady;
};

/** The signed-in Microsoft account MSAL remembers, so a token can be renewed for it. */
const currentAccount = () => msalClient?.getActiveAccount?.() ?? msalClient?.getAllAccounts?.()[0] ?? null;

/** Microsoft's own expiry for the token when it gives one; an hour otherwise (as before). */
const expiryOf = (result: any): string =>
  (result?.expiresOn ? new Date(result.expiresOn) : new Date(Date.now() + 3600000)).toISOString();

const saveSession = (result: any) => {
  session = {
    accessToken: result.accessToken,
    idToken: result.idToken,
    accessTokenExpirationDate: expiryOf(result),
  };
  localStorage.setItem('authSession', JSON.stringify(session));
};

const clearSession = () => {
  session = null;
  localStorage.removeItem('authSession');
};

/**
 * A fresh token for the remembered account, without any popup. Null when there is no
 * remembered account or Microsoft wants the user to sign in again - the login screen then
 * shows, as it always did. One renewal at a time, however many API calls ask at once.
 */
const renewSilently = (): Promise<string | null> => {
  if (!renewing) {
    renewing = (async () => {
      try {
        await initMsal();
        const account = currentAccount();
        if (!msalClient || !account) {
          clearSession();
          return null;
        }
        const result = await msalClient.acquireTokenSilent({ scopes: [AuthConfig.scope], account });
        msalClient.setActiveAccount(result.account ?? account);
        saveSession(result);
        Logger.info('AuthService', '✓ Token renewed silently');
        return result.accessToken as string;
      } catch (err) {
        Logger.debug('AuthService', 'Token refresh failed', err);
        clearSession();
        return null;
      } finally {
        renewing = null;
      }
    })();
  }
  return renewing;
};

export const authService = {
  async signIn(): Promise<{ accessToken: string; idToken: string }> {
    Logger.info('AuthService', 'Web sign-in initiated');

    try {
      await initMsal();

      if (msalClient) {
        try {
          // Try MSAL login
          const loginResult = await msalClient.loginPopup({
            scopes: [AuthConfig.scope],
          });

          // Remember who signed in, so the token can be renewed for them after a refresh.
          if (loginResult.account) msalClient.setActiveAccount(loginResult.account);
          saveSession(loginResult);
          Logger.info('AuthService', '✓ MSAL login successful');

          return {
            accessToken: loginResult.accessToken,
            idToken: loginResult.idToken,
          };
        } catch (msalErr: any) {
          if (msalErr.errorCode === 'user_cancelled') {
            throw new Error('Sign-in was cancelled');
          }
          if (POPUP_BLOCKED.includes(msalErr.errorCode)) {
            // No popup allowed: sign in on a full page instead. The page leaves for Microsoft
            // and comes back to the app signed in, where takeRedirectSignIn finishes the job.
            Logger.info('AuthService', 'Sign-in popup blocked - continuing on a full page');
            await msalClient.loginRedirect({ scopes: [AuthConfig.scope] });
            // The page is on its way to Microsoft; keep the spinner up until it has gone.
            return new Promise<never>(() => undefined);
          }
          Logger.error('AuthService', 'MSAL login failed', msalErr);
          throw msalErr;
        }
      } else {
        // Fallback: Redirect to login page or show mock login
        throw new Error(
          'Authentication library not available. Please ensure @azure/msal-browser is installed.'
        );
      }
    } catch (err) {
      Logger.error('AuthService', 'Sign-in failed', err);
      throw err;
    }
  },

  async signOut(): Promise<void> {
    Logger.info('AuthService', 'Web sign-out');

    try {
      if (msalClient) {
        await msalClient.logout();
      }
    } catch (err) {
      Logger.warn('AuthService', 'MSAL logout failed', err);
    }

    clearSession();
  },

  async getAccessToken(): Promise<string | null> {
    try {
      // Try to load from localStorage first
      const stored = localStorage.getItem('authSession');
      if (stored) {
        session = JSON.parse(stored);
        if (!session) return null;

        const expired =
          new Date(session.accessTokenExpirationDate).getTime() - EXPIRY_MARGIN_MS <= Date.now();

        // An old token is renewed quietly from MSAL's cache; only if Microsoft refuses
        // does the session end and the login screen show.
        if (expired) {
          return await renewSilently();
        }

        return session.accessToken;
      }
    } catch (err) {
      Logger.error('AuthService', 'Failed to restore session', err);
      localStorage.removeItem('authSession');
    }

    return null;
  },

  // Get access token for API calls (alias for getAccessToken on web since it already uses API scope)
  async getApiAccessToken(): Promise<string | null> {
    return this.getAccessToken();
  },

  /**
   * The tokens from a full-page Microsoft sign-in this page load has just come back from,
   * handed over once - null on any ordinary load. AuthContext finishes signing in with them,
   * exactly as after the popup.
   */
  async takeRedirectSignIn(): Promise<{ accessToken: string; idToken: string } | null> {
    await initMsal();
    const returned = redirectSignIn;
    redirectSignIn = null;
    return returned ? { accessToken: returned.accessToken, idToken: returned.idToken } : null;
  },
};
